import Alert from '@redbubble/design-system/src/Alert';
import * as constants from '@redbubble/design-system/src/constants';
import Text from '@redbubble/design-system/src/Text';
import Image from 'next/legacy/image';
import React from 'react';
import { InView } from 'react-intersection-observer';
import success from '../../public/icons/success.svg';
import { ObjectValues } from '../../types';
import styles from './Messages.module.scss';

export interface CartDiscountBannerProps<T> {
  intent: ObjectValues<typeof constants>;
  heading: string;
  messages: T[];
  getKey: (message: T, index: number) => string;
  getLineText: (message: T) => string;
  getActionUrl?: (message: T) => string | undefined;
  showIcon?: boolean;
  onLineInView?: (message: T) => void;
  // Root element the banner renders as, e.g. 'li' when mounted inside a <ul> (Cart Page),
  // or 'div' when mounted inside a plain container (Cart Tray). Defaults to 'div'.
  element?: 'li' | 'div';
}

const PromoLine = ({
  text,
  actionUrl,
  onInView,
}: {
  text: string;
  actionUrl?: string;
  onInView?: () => void;
}) => {
  const content = <Text>{text}</Text>;

  return (
    <InView as="p" className={styles.promoLine} onChange={onInView} triggerOnce>
      {actionUrl ? (
        <a href={actionUrl} className={styles.link}>
          {content}
        </a>
      ) : (
        content
      )}
    </InView>
  );
};

export const CartDiscountBanner = <T,>({
  intent,
  heading,
  messages,
  getKey,
  getLineText,
  getActionUrl,
  showIcon,
  onLineInView,
  element: Element = 'div',
}: CartDiscountBannerProps<T>) => {
  if (messages.length === 0) return null;

  return (
    <Element className={styles.promoGroup}>
      <Alert intent={intent} label={false}>
        <div className={styles.promoGroupContent}>
          {showIcon && (
            <Image
              alt=""
              layout="fixed"
              src={success}
              width={22}
              height={24}
              aria-hidden
            />
          )}
          <div>
            <Text className={styles.promoGroupHeading}>{heading}</Text>
            {messages.map((message, index) => (
              <PromoLine
                key={getKey(message, index)}
                text={getLineText(message)}
                actionUrl={getActionUrl?.(message)}
                onInView={
                  onLineInView ? () => onLineInView(message) : undefined
                }
              />
            ))}
          </div>
        </div>
      </Alert>
    </Element>
  );
};
